<template>
	<div
		id="myModal"
		class="modal"
		data-bs-focus="true"
		tabindex="-1"
		data-bs-backdrop="static"
		data-bs-keyboard="false">
		<div class="modal-dialog modal-dialog-centered modal-lg">
			<div class="modal-content">
				<div
					class="modal-header d-flex flex-column justify-content-center align-items-center fw-bold">
					<div class="text-center">
						<h1>{{ props.text_cam.title }}</h1>
						<div
							class="text-muted fw-semibold fs-5"
							v-if="props.close_class">
							Valide sua biometria facial antes de finalizar a
							aula
						</div>
						<div class="text-muted fw-semibold fs-5" v-else>
							Valide sua biometria facial antes de iniciar a aula
						</div>
					</div>
					<span
						class="text-info text-center fw-semibold fs-5"
						v-if="props.close_class">
						Você tem {{ props.additional_time.minutes }}m
						{{ props.additional_time.seconds }}s para finalizar a
						aula!
					</span>
					<div v-if="props.close_class" class="fw-bold fs-5" style="color: red;">
						<strong>Por favor, clique em sair somente quando concluir o fechamento da aula.</strong>
					</div>					
				</div>

                <div
                    v-if="!props.browser_has_all_media_permissions"
                    class="p-5 fs-4">
                    <div class="alert alert-danger">
                        <ul class="m-0">
                            <li>
                                Você precisa permitir que o navegador acesse a
                                câmera para fazer a biometria.
                            </li>
                            <li>
                                Após liberar a permissão para acesso da câmera,
                                atualize a página.
                            </li>
                        </ul>
                    </div>
                </div>

				<div class="modal-body" v-else>
					<div class="d-flex flex-column">
						<li
							class="d-flex align-items-center p-1"
							:key="text"
							v-for="text in props.text_cam.text_body">
							<span class="bullet me-5"></span>
							<span
								class="text-dark fw-semibold fs-5"
								v-html="text"></span>
						</li>
					</div>

					<div class="row justify-content-md-center mt-2">
						<div
							class="col col-lg-12 d-flex align-items-center justify-content-center border rounded"
							style="
								background-color: #1e1e2d;
								width: 450px;
								height: 350px;
							">
							<div v-if="!mock_student_photo">
								<div
									id="spinner"
									class="spinner-border spinner-border-xl"
									style="
										color: coral;
										z-index: 2;
										position: relative;
									"
									role="status">
									<span class="sr-only"> Carregando... </span>
								</div>
								<div id="camera" class="camera"></div>
							</div>
							<img
								v-else
								width="320"
								:src="`data:image/jpeg;base64,${props.mock_user_photo_b64}`" />
						</div>
					</div>

					<error v-if="error_status" :error_message="error_message" />

                    <div
                        v-if="props.biometry_tries === props.biometry_tries_limit - 1"
                        class="alert alert-warning d-flex align-items-center mt-3 p-5 fw-bold">
                        <i class="fa-solid fa-triangle-exclamation fa-2xl me-3"></i>
                        <p class="m-0 fs-1">Atenção: você tem somente 1 tentativa restante!</p>
                    </div>

					<div class="row justify-content-md-center mt-5">
						<button
							id="button-take-snapshot"
							class="btn btn-block btn-primary"
							href="javascript:void(0);"
							:disabled="loading || props.biometry_tries >= props.biometry_tries_limit"
							@click="take_snapshot(props.urlRoute as string)">
							<div v-if="loading">
								<i class="fa fa-spin fa-sync"></i> Carregando...
							</div>
							<span v-else>Capturar foto - Tentativas {{ props.biometry_tries }} / {{ props.biometry_tries_limit }}</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { usePage } from '@inertiajs/vue3';
    import { onMounted, PropType, reactive, ref } from 'vue';
    import axios, { AxiosError } from 'axios';
	import { getPhoto } from '../../utils/requestModalWebcam';
	import error from './error_message.vue';
	import $ from 'jquery';
	import Swal from 'sweetalert2';

	defineExpose({ closeModal, openModal });

	const emit = defineEmits(['biometry_successful', 'biometry_executed', 'partialReload']);
	const error_status = ref(false);
	const error_message = ref('');

	interface TextCam {
		title: string;
		text_body: Array<string>;
	}

	const props = defineProps({
		mock_user_photo_b64: { type: String, required: true },
		urlRoute: { type: String, required: true },
		text_cam: { type: Object as PropType<TextCam>, required: true },
		additional_time: { type: Object, required: true },
		close_class: { type: Boolean, required: true },
        get_class_data: {type: Object, required: true},
        api_token: {type: String, required: true},
        browser_has_all_media_permissions: {type: Boolean, required: true},
        biometry_tries: {type: Number, required: true},
        biometry_tries_limit: {type: Number, required: true},
	});

	const mock_student_photo = usePage().props?.mock_user_photo as boolean;

	let modal: any = null;
	const loading = ref(false);

	function attachWebcam() {
		$('#spinner').show();
		$('#button-take-snapshot').hide();
		$('#camera').hide();

		window.Webcam.set({
			width: 350,
			height: 250,
			image_format: 'jpeg',
			jpeg_quality: 100,
		});

		window.Webcam.attach('#camera');

		window.Webcam.on('live', function () {
			$('#spinner').hide();
			$('#camera').show();
			$('#button-take-snapshot').show();
		});
	}

	async function take_snapshot(store_photo_url: string) {
        loading.value = true;

		try {
            const {class_accepting_student_biometry} = await axios.get(props.get_class_data.url,{
                headers: {
                    Authorization: props.api_token
                },
            }).then(response => response.data.data);

            if(!class_accepting_student_biometry) {
                alert('Não é mais possível receber a biometria para esta aula.');

                closeModal();

                emit('partialReload');

                return;
            }

            reset_error();

            const b64 = await getPhoto(props.mock_user_photo_b64);

            const response = await axios.post(store_photo_url, { base64_file: b64 });

            alert(response.data.message);

            closeModal();

            emit('biometry_successful');
		} catch (err) {
			if (axios.isAxiosError(err)) {
                if (err.code === "ERR_NETWORK") {
                    set_message_error('Sem conexão com a internet. Por favor, verifique e tente novamente.')

                    return;
                }

				if (err.response?.status === 401) {
					setTimeout(() => {
						window.location.href = '/logout';
					}, 4000);

					Swal.fire({
						icon: 'error',
						title: 'Sua sessão expirou. Por favor, faça login novamente!',
						text: 'Você será redirecionado para o login em 4 segundos.',
						timerProgressBar: true,
						timer: 4000,
						allowOutsideClick: false,
						showConfirmButton: false,
					});

                    return;
                }

                set_message_error(err.response?.data.message);
			} else {
				set_message_error(`${err}`);
			}

            emit('biometry_executed');
		} finally {
			loading.value = false;
		}
	}

	function reset_error() {
		error_status.value = false;
		error_message.value = '';
	}

	function set_message_error(message) {
		error_status.value = true;
		error_message.value = message;
	}

	function closeModal() {
		modal.hide();
	}

    function openModal() {
        modal.show();
    }


	onMounted(async () => {
        //@ts-ignore
		modal = new bootstrap.Modal('#myModal');

		if (!mock_student_photo && props.browser_has_all_media_permissions) {
			attachWebcam();
		}
	});
</script>

<style scoped>
	button {
		font-weight: bold;
	}
</style>
